import { useEffect, useState } from 'react';
import { useNavigate } from '@/lib/router-dom';
import useSchemaForDataElement from './useSchemaForDataElement';
import submittalsService from '@/services/api/submittalsService.js';

/**
 * Loads a submittal by ID, then loads the schema for its data element and
 * seeds the payload. Used by the read-only detail page.
 *
 * Returns { submittal, schema, payload, setPayload, activityLogs, isLoading }.
 * `isLoading` is true until all three (submittal, schema, payload) are ready.
 * Navigates back to /submittals on a load error after surfacing a toast.
 */
const useSubmittalDetail = ({ submittalId, addToastMessage }) => {
    const { goTo } = useNavigate();
    const [submittal, setSubmittal] = useState(null);
    const [activityLogs, setActivityLogs] = useState([]);

    const { schema, payload, setPayload } = useSchemaForDataElement({
        dataElementId: submittal?.data_element_id,
        existingPayload: submittal?.payload || null,
    });

    useEffect(() => {
        if (!submittalId) return;
        submittalsService.getSubmittalById(submittalId).then(([err, data]) => {
            if (err) {
                addToastMessage(err, 'error');
                goTo('/submittals');
                return;
            }
            setSubmittal(data);
        });
        submittalsService.getActivityLogs(submittalId).then(([err, logs]) => {
            if (!err && logs) setActivityLogs(logs);
        });
    }, [submittalId]);

    const isLoading = !submittal || !schema || !payload;

    return { submittal, schema, payload, setPayload, activityLogs, isLoading };
};

export default useSubmittalDetail;
